import { useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import Layout from "./components/Layout/Layout";
import { PageSkeleton } from "./components/ui/Skeleton";
import AdminRoute from "./components/Routes/AdminRoute";
import UserRoute from "./components/Routes/UserRoute";
import { useSiteConfig } from "./contexts/SiteConfigContext";

import { loadUser } from "./actions/reduxActions/userActions";
import { LOAD_USER_FAIL } from "./constants/userConstants";
import store from "./store";

import PublicRoutes from "./routes/PublicRoutes";
import UserRoutes from "./routes/UserRoutes";
import AdminRoutes from "./routes/AdminRoutes";

// Background-route overlays (listing details rendered on top of the map page
// via location.state.background* — preserved from v5).
const ListingDetails = lazy(() => import("./components/Public/ListingDetails"));
const AdminListingDetails = lazy(() =>
  import("./components/Admin/Listing/AdminListingDetails")
);
const UserListingDetails = lazy(() =>
  import("./components/User/UserListingDetails")
);

const TRACKING_ID =
  import.meta.env?.VITE_GOOGLE_ANALYTICS_ID ||
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_GOOGLE_ANALYTICS_ID);
if (TRACKING_ID) {
  ReactGA.initialize(TRACKING_ID);
  ReactGA.send("pageview");
}

// Brand blue. Shown only while a lazy route chunk is actually loading (the
// Suspense fallback below), so it reflects real work instead of a fixed timer.
TopBarProgress.config({
  barColors: { 0: "#006aff", "1.0": "#4d97ff" },
  barThickness: 3,
  shadowBlur: 0,
});

const App = () => {
  const [t, i18n] = useTranslation("common");
  const location = useLocation();
  const site = useSiteConfig();

  useEffect(() => {
    const userId = JSON.parse(window.localStorage.getItem("userId"));
    if (userId) {
      store.dispatch(loadUser());
    } else {
      store.dispatch({ type: LOAD_USER_FAIL });
    }
  }, []);

  const backgroundPublic = location.state && location.state.backgroundPublic;
  const backgroundUser = location.state && location.state.backgroundUser;
  const backgroundAdmin = location.state && location.state.backgroundAdmin;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const routesLocation =
    backgroundPublic || backgroundUser || backgroundAdmin || location;

  return (
    <Layout
      t={t}
      i18n={i18n}
      gmail={site.gmail}
      twitter={site.twitter}
      facebook={site.facebook}
      instagram={site.instagram}
      copyright={site.copyright}
      message={site.message}
      showMessage={site.showMessage}
    >
      <Suspense
        fallback={
          <>
            <TopBarProgress />
            <PageSkeleton />
          </>
        }
      >
        <Routes location={routesLocation}>
          {PublicRoutes({ t, i18n })}
          {UserRoutes()}
          {AdminRoutes({ t, i18n })}
        </Routes>

        {/* Modal-overlay routes: rendered on top of the underlying Routes when
            navigation carried a `background*` state. Own Suspense boundary so
            loading the overlay chunk doesn't replace the page underneath. */}
        <Suspense fallback={<TopBarProgress />}>
          {backgroundAdmin && (
            <Routes>
              <Route
                path="/admin/property-details/:slug/:id"
                element={<AdminRoute><AdminListingDetails /></AdminRoute>}
              />
            </Routes>
          )}
          {backgroundUser && (
            <Routes>
              <Route
                path="/my/property-details/:slug/:id"
                element={<UserRoute><UserListingDetails /></UserRoute>}
              />
            </Routes>
          )}
          {backgroundPublic && (
            <Routes>
              <Route
                path="/property-details/:slug/:id"
                element={
                  <ListingDetails
                    termsContact={site.termsContact}
                    termsContactUrdu={site.termsContactUrdu}
                  />
                }
              />
            </Routes>
          )}
        </Suspense>
      </Suspense>
    </Layout>
  );
};

export default App;
